'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useId, useMemo, useRef, useState, } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cx } from '../../../internal/cx.js';
import { CloseGlyph } from '../../../internal/icons.js';
import { useFieldControl } from '../form-field/index.js';
const defaultMessages = {
    duplicate: (tag) => `“${tag}” is already added.`,
    max: (max) => `You can add up to ${max}.`,
    added: (tag) => `Added ${tag}.`,
    removed: (tag) => `Removed ${tag}.`,
    suggestions: 'Suggestions',
};
const defaultFilter = (s, q) => s.toLowerCase().includes(q.toLowerCase());
const defaultNormalize = (raw) => raw.trim();
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/**
 * Edits a list of short values as removable chips: skills, labels, recipient
 * addresses. Enter or a separator adds the typed text, pasted lists are split,
 * a validator can reject values, and optional suggestions appear in a listbox.
 */
export const TagInput = forwardRef(function TagInput({ value: valueProp, defaultValue = [], onValueChange, inputValue: inputProp, onInputValueChange, separators = [','], normalize = defaultNormalize, validate, allowDuplicates = false, max, suggestions, filterSuggestions = defaultFilter, addOnBlur = false, removeLabel = (tag) => `Remove ${tag}`, messages: messagesProp, name, size = 'md', invalid, className, placeholder, onKeyDown, onBlur, onPaste, ...rest }, ref) {
    const id = useId();
    const listId = `mn-tag-input-${id}-list`;
    const messageId = `mn-tag-input-${id}-message`;
    const messages = { ...defaultMessages, ...messagesProp };
    const [internalTags, setInternalTags] = useState(defaultValue);
    const tags = valueProp !== undefined ? valueProp : internalTags;
    const [internalInput, setInternalInput] = useState('');
    const text = inputProp !== undefined ? inputProp : internalInput;
    const [message, setMessage] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const inputRef = useRef(null);
    const controlRef = useRef(null);
    const removeRefs = useRef([]);
    const focusAfter = useRef(null);
    const setTags = (next) => {
        if (valueProp === undefined)
            setInternalTags(next);
        onValueChange?.(next);
    };
    const setText = (next) => {
        if (inputProp === undefined)
            setInternalInput(next);
        onInputValueChange?.(next);
    };
    // Move focus once the list has re-rendered after a removal.
    useEffect(() => {
        const target = focusAfter.current;
        if (target === null)
            return;
        focusAfter.current = null;
        if (target === 'input')
            inputRef.current?.focus();
        else
            (removeRefs.current[target] ?? inputRef.current)?.focus();
    }, [tags]);
    const fieldProps = useFieldControl({
        ...rest,
        'aria-describedby': cx(rest['aria-describedby'], message ? messageId : undefined),
    }, { invalid: invalid || (message ? true : undefined) });
    const disabled = !!fieldProps.disabled;
    const readOnly = !!rest.readOnly;
    const editable = !disabled && !readOnly;
    const visible = useMemo(() => {
        if (!suggestions)
            return [];
        const taken = new Set(tags.map((t) => t.toLowerCase()));
        const q = text.trim();
        return suggestions.filter((s) => !taken.has(s.toLowerCase()) && (q === '' || filterSuggestions(s, q)));
    }, [suggestions, tags, text, filterSuggestions]);
    const listOpen = open && visible.length > 0 && editable;
    useEffect(() => {
        setActive((a) => Math.min(a, visible.length - 1));
    }, [visible.length]);
    /** Adds each value in order; returns the ones that were rejected. */
    const addMany = (raws) => {
        const next = [...tags];
        const added = [];
        const rejected = [];
        let error = null;
        for (const raw of raws) {
            const tag = normalize(raw);
            if (!tag)
                continue;
            let reason = null;
            if (max !== undefined && next.length >= max)
                reason = messages.max(max);
            else if (!allowDuplicates && next.some((t) => t.toLowerCase() === tag.toLowerCase()))
                reason = messages.duplicate(tag);
            else
                reason = validate?.(tag, next);
            if (reason) {
                error ??= reason;
                rejected.push(raw.trim());
            }
            else {
                next.push(tag);
                added.push(tag);
            }
        }
        if (added.length > 0) {
            setTags(next);
            setAnnouncement(added.map((t) => messages.added(t)).join(' '));
        }
        setMessage(error);
        return rejected;
    };
    const commitText = (raw = text) => {
        if (raw.trim() === '')
            return;
        const rejected = addMany([raw]);
        setText(rejected.join(', '));
        setActive(-1);
    };
    const remove = (index, focus) => {
        const tag = tags[index];
        if (tag === undefined || !editable)
            return;
        focusAfter.current = focus;
        setTags(tags.filter((_, i) => i !== index));
        setAnnouncement(messages.removed(tag));
        setMessage(null);
    };
    const focusRemove = (index) => {
        if (index < 0)
            return;
        if (index >= tags.length)
            inputRef.current?.focus();
        else
            removeRefs.current[index]?.focus();
    };
    const handleInputKey = (e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented || !editable)
            return;
        const caretAtStart = e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === 0;
        if (e.key === 'Enter') {
            if (listOpen && active >= 0 && visible[active] !== undefined) {
                e.preventDefault();
                commitText(visible[active]);
                setOpen(false);
            }
            else if (text.trim() !== '') {
                e.preventDefault();
                commitText();
            }
        }
        else if (separators.includes(e.key)) {
            e.preventDefault();
            commitText();
        }
        else if (e.key === 'Backspace' && text === '' && tags.length > 0) {
            e.preventDefault();
            focusRemove(tags.length - 1);
        }
        else if (e.key === 'ArrowLeft' && caretAtStart && tags.length > 0) {
            e.preventDefault();
            focusRemove(tags.length - 1);
        }
        else if (e.key === 'ArrowDown' && suggestions) {
            e.preventDefault();
            if (!open)
                setOpen(true);
            setActive((a) => Math.min(a + 1, visible.length - 1));
        }
        else if (e.key === 'ArrowUp' && suggestions) {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
        }
        else if (e.key === 'Escape' && listOpen) {
            e.preventDefault();
            setOpen(false);
            setActive(-1);
        }
    };
    const handleRemoveKey = (e, index) => {
        switch (e.key) {
            case 'Backspace':
                e.preventDefault();
                remove(index, index > 0 ? index - 1 : tags.length > 1 ? 0 : 'input');
                break;
            case 'Delete':
                e.preventDefault();
                remove(index, index < tags.length - 1 ? index : 'input');
                break;
            case 'ArrowLeft':
                e.preventDefault();
                focusRemove(Math.max(index - 1, 0));
                break;
            case 'ArrowRight':
                e.preventDefault();
                focusRemove(index + 1);
                break;
        }
    };
    const handlePaste = (e) => {
        onPaste?.(e);
        if (e.defaultPrevented || !editable)
            return;
        const pasted = e.clipboardData.getData('text');
        const splitter = new RegExp(`[${separators.map(escapeRe).join('')}\\n\\r\\t]`);
        if (!splitter.test(pasted))
            return;
        e.preventDefault();
        const rejected = addMany((text + pasted).split(splitter));
        setText(rejected.join(', '));
    };
    const activeId = listOpen && active >= 0 ? `${listId}-opt-${active}` : undefined;
    const comboboxProps = suggestions
        ? {
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': listOpen,
            'aria-controls': listOpen ? listId : undefined,
            'aria-activedescendant': activeId,
        }
        : {};
    return (_jsxs("div", { className: cx('mn-tag-input', className), "data-size": size, children: [_jsxs(PopoverPrimitive.Root, { open: listOpen, onOpenChange: setOpen, children: [_jsx(PopoverPrimitive.Anchor, { asChild: true, children: _jsxs("div", { ref: controlRef, className: "mn-input mn-tag-input__control", "data-size": size, "data-disabled": disabled || undefined, "data-invalid": fieldProps['aria-invalid'] ? true : undefined, children: [tags.length > 0 ? (_jsx("ul", { className: "mn-tag-input__list", children: tags.map((tag, i) => (_jsxs("li", { className: "mn-tag-input__tag", children: [_jsx("span", { className: "mn-tag-input__label", children: tag }), editable ? (_jsx("button", { ref: (node) => {
                                                    removeRefs.current[i] = node;
                                                }, type: "button", className: "mn-tag-input__remove", "aria-label": removeLabel(tag), onClick: () => remove(i, 'input'), onKeyDown: (e) => handleRemoveKey(e, i), children: _jsx(CloseGlyph, {}) })) : null] }, `${tag}-${i}`))) })) : null, _jsx("input", { ref: (node) => {
                                        inputRef.current = node;
                                        if (typeof ref === 'function')
                                            ref(node);
                                        else if (ref)
                                            ref.current = node;
                                    }, autoComplete: "off", ...fieldProps, ...comboboxProps, type: "text", className: "mn-tag-input__input", placeholder: tags.length === 0 ? placeholder : undefined, value: text, onChange: (e) => {
                                        setText(e.target.value);
                                        setMessage(null);
                                        setActive(-1);
                                        if (suggestions)
                                            setOpen(true);
                                    }, onKeyDown: handleInputKey, onPaste: handlePaste, onBlur: (e) => {
                                        onBlur?.(e);
                                        const into = e.relatedTarget;
                                        if (controlRef.current?.contains(into))
                                            return;
                                        if (document.getElementById(listId)?.contains(into))
                                            return;
                                        if (addOnBlur && editable)
                                            commitText();
                                        setOpen(false);
                                    } })] }) }), suggestions ? (_jsx(PopoverPrimitive.Portal, { children: _jsx(PopoverPrimitive.Content, { align: "start", sideOffset: 4, onOpenAutoFocus: (e) => e.preventDefault(), onCloseAutoFocus: (e) => e.preventDefault(), onInteractOutside: (e) => {
                                if (controlRef.current?.contains(e.target))
                                    e.preventDefault();
                            }, className: "mn-menu mn-tag-input__content", children: _jsx("div", { id: listId, role: "listbox", "aria-label": messages.suggestions, className: "mn-tag-input__listbox", children: visible.map((s, i) => (
                                // aria-activedescendant pattern: the input keeps focus and handles every key.
                                // monarch-ignore-next-line a11y/click-without-key -- aria-activedescendant pattern: the input handles all keys
                                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
                                _jsx("div", { id: `${listId}-opt-${i}`, role: "option", "aria-selected": i === active, "data-highlighted": i === active || undefined, className: "mn-menu__item", onMouseEnter: () => setActive(i), onMouseDown: (e) => e.preventDefault(), onClick: () => {
                                        commitText(s);
                                        inputRef.current?.focus();
                                    }, children: _jsx("span", { className: "mn-menu__text", children: s }) }, s))) }) }) })) : null] }), message ? (_jsx("p", { id: messageId, className: "mn-tag-input__message", "aria-live": "polite", children: message })) : null, _jsx("span", { className: "mn-visually-hidden", role: "status", children: announcement }), name ? tags.map((tag, i) => _jsx("input", { type: "hidden", name: name, value: tag }, i)) : null] }));
});
//# sourceMappingURL=tag-input.js.map